import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Send, AtSign, Mic, Paperclip, X, FileText, Image as ImageIcon, Table, Clapperboard, Eye } from 'lucide-react';
import MessageStream from './MessageStream';
import ChatThreadHeader from './ChatThreadHeader';
import AgentDetailPanel from './AgentDetailPanel';
import DecisionBriefPanel from './DecisionBriefPanel';
import ChatActivityBar from './ChatActivityBar';
import RetentionNudge from './RetentionNudge';
import MemberAvatar from './MemberAvatar';
import { inferActivity } from '../../utils/chatActivity';
import GenerationOptions from './GenerationOptions';
import CreateMenu from './CreateMenu';
import StudioPanel from '../studio/StudioPanel';
import { PaneRail, PaneResizeHandle, PaneToggleRow, UnreadDot } from './PaneChrome';
import { SIDE_PANE_WIDTH } from '../../constants/layout';
import type { PaneResizeProps } from '../../hooks/useSidePane';
import StudioHome from './StudioHome';
import { takeOnboardingIntent, purposeByKey } from '../onboarding';
import GroupNotesPanel from './GroupNotesPanel';
import type { StreamDef } from '../studio/StreamPicker';
import { detectMediaIntent, buildMusicStyle, buildMusicKind, DEFAULT_ASPECT, DEFAULT_SIZE, DEFAULT_SONG_LENGTH, type MusicChoice } from '../../utils/mediaIntent';
import { addSuggestedAgent, type ConversationSummary } from '../../services/conversationService';
import { detectMention, applyMention, appendMention, resolveMentionTargets } from './mentions';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { downloadTranscript } from '../../utils/exportTranscript';
import { validateAttachments, formatBytes, type AcceptedAttachment, type AttachmentKind } from '../../utils/chatAttachments';
import { useCatalogAvatars } from './useCatalogAvatars';
import type { ArtifactKind } from '../../services/artifactService';
import type { ChatSession, MemberSpec } from './types';
import { TYPE } from '../../constants/typeScale';

const KIND_ICON: Record<AttachmentKind, React.FC<{ className?: string }>> = {
  document: FileText, image: ImageIcon, data: Table,
};

// Media Studio Phase A — "Animate this" (image-to-video). SHIP-DARK behind a build flag; the server
// gates the same feature with I2V_ENABLED. Both must be on (and verified on staging) before it's live.
const I2V_ENABLED = process.env.REACT_APP_I2V_ENABLED === '1';
const ANIMATE_CUE = /\b(animate|make it move|come alive|into a (video|clip)|add motion)\b/i;
const EMPTY_MEMBERS: MemberSpec[] = []; // stable ref so mem[members] holds when there's no session yet

interface Props {
  session: ChatSession | null;
  sending: boolean;
  respondingIds: string[];
  onSend: (text: string, targetIds?: string[], opts?: { expectReply?: boolean; attachments?: AcceptedAttachment[]; mediaAspect?: string; mediaSize?: string; musicStyle?: string; musicKind?: string; mediaStyle?: string; cameraMove?: string; durationSec?: number }) => void;
  /** Export the chat as an artifact (the card streams back in via the message feed). */
  onExport?: (kind: ArtifactKind, format?: 'html') => Promise<void>;
  /** Download a stored artifact by id (from an artifact card). */
  onDownloadArtifact?: (artifactId: string) => void;
  /** Toggle the caller's emoji reaction on a message. */
  onReact?: (messageId: string, emoji: string) => void;
  /** Owner-only: bookmark/unbookmark a message. */
  onBookmark?: (messageId: string, bookmarked: boolean) => void;
  /** Owner-only: delete a message. */
  onDeleteMessage?: (messageId: string) => void;
  /** Open the invite modal (rendered by the parent). */
  onInvite?: () => void;
  /** Open the manage-members panel (rendered by the parent). */
  onManage?: () => void;
  /** The current user's uid — to tell their messages apart from other humans'. */
  myUid?: string;
  /** True when the current user owns this chat (can rename agents / set avatars). */
  isOwner?: boolean;
  /** Propagate a conversation update (e.g. agent alias/avatar changed). */
  onUpdated?: (c: ConversationSummary) => void;
  /** Refresh the wallet balance after a spend. */
  onWalletChanged?: () => void;
  /** FREE plan → exports carry the wisetree.ai watermark; paid removes it. */
  branded?: boolean;
  /** Mobile only: return to the conversation list (list↔detail navigation). */
  onBack?: () => void;
  /** The left pane's current state on desktop — the Studio reserves exactly that much room. */
  sidebarVisible?: 'open' | 'rail';
  /** The left pane's LIVE width, so a dragged border moves the Studio's edge with it. */
  sidebarWidth?: string;
  /**
   * THE RIGHT PANE, owned by the workspace (which also owns the left one) so the two stay symmetric and both
   * remember themselves across reloads. We render its chrome — the toggle on its centre-facing edge, the
   * draggable inner border, and the rail it folds down to — around whichever level is showing.
   */
  rightPane?: {
    open: boolean;
    /** A CSS length: the shared token, or the creator's dragged width. */
    width: string;
    /** Crew replies the creator has not seen — the folded rail must say so, or the pane hides them silently. */
    unread?: boolean;
    onToggle: () => void;
    resize: PaneResizeProps;
    paneRef?: React.RefObject<HTMLDivElement>;
  };
  /** True while EITHER border is being dragged — kills the easing so the canvas tracks the pointer 1:1. */
  panesDragging?: boolean;
  /** FR4: open the secondary chat list from the launchpad (mobile — desktop already shows the sidebar). */
  onOpenChats?: () => void;
  /**
   * What the workstation's right pane shows when the open production has no crew yet, rendered by the host
   * (it owns session
   * selection + the chat modals). Shown in the rail whenever no chat is open; picking one navigates the rail
   * to LEVEL 2, the chat itself. Desktop only — mobile keeps its list↔detail flip.
   */
  paneEmptyState?: React.ReactNode;
  /** The open production's crew chat — the Studio only trusts a roster that belongs to THIS production. */
  crewChatId?: string | null;
  /**
   * WHO IS ON THAT CREW, from the host's own chat list rather than from whichever chat happens to be mounted.
   * Reading the roster off the open session emptied it the moment the creator opened a different chat in the
   * pane — which is exactly when the Studio's crew-derived seats (the writer, the director) would blank out.
   */
  crewMemberIds?: string[];
  /** Told which crew chat the open production owns, so the host can land the right pane on it. */
  onCrewChatResolved?: (info: { conversationId: string | null; conversation?: ConversationSummary }) => void;
}

const ChatThread: React.FC<Props> = ({ session, sending, respondingIds, onSend, onExport, onDownloadArtifact, onReact, onBookmark, onDeleteMessage, onInvite, onManage, myUid, isOwner, onUpdated, onWalletChanged, branded, onBack, onOpenChats, sidebarVisible, sidebarWidth, rightPane, panesDragging, paneEmptyState, crewChatId, crewMemberIds, onCrewChatResolved }) => {
  const [agentDetail, setAgentDetail] = useState<MemberSpec | null>(null);
  const [showBrief, setShowBrief] = useState(false);
  const [text, setText] = useState('');
  const [mention, setMention] = useState<{ query: string; start: number } | null>(null);
  const [exporting, setExporting] = useState<ArtifactKind | null>(null);
  const [exportErr, setExportErr] = useState('');
  const [attachments, setAttachments] = useState<AcceptedAttachment[]>([]);
  const [attachError, setAttachError] = useState('');
  const [dragging, setDragging] = useState(false);
  // Pre-generation pickers: a dimension (image/video) or singer choice (music) the user can
  // set before sending. Driven by the draft's detected media intent.
  const [aspect, setAspect] = useState(DEFAULT_ASPECT);
  const [size, setSize] = useState(DEFAULT_SIZE); // output resolution: 'standard' | 'hd'
  const [music, setMusic] = useState<MusicChoice>({});
  const [style, setStyle] = useState(''); // prompt-factory style preset key ('' = none)
  const [cameraMove, setCameraMove] = useState(''); // named camera move (video only; '' = none)
  const [studioOpen, setStudioOpen] = useState(false); // the multi-shot Video Studio workspace (right pane)
  // When the Studio EXPANDS it takes the main region and leaves a right rail; the chat narrows into that rail
  // instead of sitting hidden behind it, so the creator can keep talking to the crew while they build.
  const [studioExpanded, setStudioExpanded] = useState(false);
  // The right pane can be COLLAPSED so the studio gets the whole screen when the creator is heads-down
  // building, and slid back in when they want the crew. The workspace owns that state (it is persisted and
  // shared with the left pane's); with no host — a bare /group chat, a test — the pane is simply always open.
  const chatRailOpen = rightPane?.open ?? true;
  const paneWidth = rightPane?.width ?? SIDE_PANE_WIDTH;
  // Studio launch context from the dashboard StudioHome: a chosen work stream (skip the picker), or an existing
  // production to reopen. Both null when the Studio is opened the normal way (via CreateMenu inside a chat).
  const [initialStream, setInitialStream] = useState<StreamDef | null>(null);
  const [openStoryboardId, setOpenStoryboardId] = useState<string | null>(null);
  /**
   * An open production is ADDRESSABLE: /dashboard/{slug}.
   *
   * Before this the Studio was pure component state, so a production could not be linked, bookmarked, or
   * returned to with the back button — and a reload always dropped you back on the launchpad. The handle is a
   * slug when the production has one and its id otherwise, because storyboards created before slugs exist
   * have only an id; every consumer resolves either.
   */
  const { production: productionParam, stage: stageParam } = useParams<{ production: string; stage: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /** Open a production AND reflect it in the URL. */
  const openProduction = useCallback((handle: string) => {
    setOpenStoryboardId(handle);
    setStudioOpen(true);
    navigate(`/dashboard/${handle}`, { replace: true });
  }, [navigate]);

  /**
   * Each stage is a dedicated page: /dashboard/{slug}/{stage}. A production that has not been saved yet has
   * no handle, so its stages live under `new` — the journey stays linkable from the very first screen rather
   * than only after the first save.
   *
   * Each move PUSHES a history entry, so the browser's Back walks back through the stages the creator took
   * instead of leaving the studio (it used to replace, and Back from Publish landed on whatever page came
   * before the dashboard). The automatic resume never comes through here — it sets the stage locally.
   */
  const goStage = useCallback((stage: string) => {
    navigate(`/dashboard/${productionParam || 'new'}/${stage}`);
  }, [navigate, productionParam]);

  /**
   * A NEW EPISODE TAKES ITS REAL ADDRESS ON ITS FIRST SAVE (BUGS.md 2026-09-27). It used to stay on
   * /dashboard/new/{stage}, so a reload opened a blank episode and the work looked unsaved. The address is
   * REPLACED (Back must not return to /new), and the handle is remembered so the deep-link effect below does not
   * reload an episode the Studio is already holding — a reload there would overwrite what was typed since.
   */
  const adoptedHandle = useRef<string | null>(null);
  // Read at call time, not capture time: the create resolves after a round-trip, and the creator may have
  // stepped to another stage (another URL) while it was in flight.
  const route = useRef({ productionParam, stageParam });
  route.current = { productionParam, stageParam };
  const onEpisodeCreated = useCallback((handle: string) => {
    const { productionParam: current, stageParam: currentStage } = route.current;
    if (current !== 'new') return;
    adoptedHandle.current = handle;
    navigate(`/dashboard/${handle}${currentStage ? `/${currentStage}` : ''}`, { replace: true });
  }, [navigate]);

  /** Deep link: /dashboard/{handle} opens that production on arrival. */
  useEffect(() => {
    if (!productionParam) return;
    if (adoptedHandle.current === productionParam) { adoptedHandle.current = null; return; }
    // `new` is the placeholder handle for a production that has not been saved yet — there is nothing to
    // load, but the Studio should still open so its stage URL works from the first screen.
    setOpenStoryboardId(productionParam === 'new' ? null : productionParam);
    setStudioOpen(true);
  }, [productionParam]);
  const [notesOpen, setNotesOpen] = useState(false); // the owner's "Requests for your crew" panel (A2)
  const isDesktopView = useIsDesktop();
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const catalogAvatars = useCatalogAvatars(); // admin default avatars, keyed by profile_id

  // Validate dropped/attached files against caps + allowlist; keep the good ones,
  // surface a friendly reason for any rejected.
  const addFiles = (files: File[]) => {
    if (!files.length) return;
    const { accepted, rejected } = validateAttachments(files, attachments.length);
    if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
    setAttachError(rejected.length ? rejected.map((r) => `${r.name}: ${r.reason}`).join(' · ') : '');
  };
  const removeAttachment = (i: number) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  // Browser-native voice input: each finalized chunk is appended to the composer.
  const speech = useSpeechRecognition({
    onResult: (chunk) => setText((prev) => (prev.trim() ? `${prev.replace(/\s*$/, '')} ${chunk}` : chunk)),
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.messages.length, sending]);

  // Purpose-first onboarding handoff: a creator who picked a purpose on the landing lands here after sign-in.
  // Read the intent ONCE and auto-open the Studio into that purpose's default stream (crew assembled, first
  // prompt suggested) — zero empty screens. Only fires on the dashboard launchpad (no chat open) and only for
  // an AVAILABLE purpose with a stream (photobook is teased until its pipeline ships).
  const onboardingConsumed = useRef(false);
  useEffect(() => {
    if (onboardingConsumed.current || session) return;
    const intent = takeOnboardingIntent();
    if (!intent) return;
    onboardingConsumed.current = true;
    const stream = purposeByKey(intent.purpose)?.defaultStream;
    if (stream) { setInitialStream(stream); setStudioOpen(true); }
  }, [session]);

  // Derived values passed to the (memoized) MessageStream — MEMOIZED so a keystroke in the composer (which only
  // changes `text`/`mention`) doesn't hand MessageStream new prop references and force a full re-render of the list.
  const members = session?.members ?? EMPTY_MEMBERS;
  const messages = session?.messages;
  const memberNames = useMemo(() => members.map((m) => m.name), [members]);
  const { repliedSince, responding, lastUser } = useMemo(() => {
    const msgs = messages ?? [];
    let lastIdx = -1;
    for (let i = msgs.length - 1; i >= 0; i--) { if (msgs[i].author === 'you') { lastIdx = i; break; } }
    const replied = new Set(msgs.slice(lastIdx + 1).map((m) => m.author));
    return { repliedSince: replied, responding: members.filter((m) => respondingIds.includes(m.id) && !replied.has(m.id)), lastUser: lastIdx >= 0 ? msgs[lastIdx] : null };
  }, [messages, members, respondingIds]);
  // Non-intrusive activity label for the in-flight turn, inferred from the latest ask.
  const activity = useMemo(
    () => (sending && lastUser ? inferActivity(lastUser.content, (lastUser.attachments || []).some((a) => a.kind === 'image')) : null),
    [sending, lastUser],
  );
  // Stable callbacks handed to MessageStream (identity must survive keystroke re-renders for the memo to hold).
  const insertMention = useCallback((name: string) => { setText((t) => appendMention(t, name)); taRef.current?.focus(); }, []);
  const handleAddAgent = useCallback(async (agentId: string) => {
    if (!session) return;
    const updated = await addSuggestedAgent(session.id, agentId);
    onUpdated?.(updated);
  }, [session, onUpdated]);
  const selectAgent = useCallback((mem: MemberSpec) => setAgentDetail(mem), []);

  /**
   * THE STUDIO IS THE DASHBOARD on a workstation (RF3-WORKSTATION): under /dashboard it is the surface
   * itself, open whether or not a chat is selected in the right pane. /group/{slug} is unchanged — there the
   * chat owns the screen and the Studio only opens when the creator asks for it.
   */
  const studioIsSurface = isDesktopView && !pathname.startsWith('/group');
  /**
   * A REAL HOME (navigation review NAV-8, owner 2026-09-26). The workstation frame (both panes) is always the
   * surface, but the Studio only covers the centre once the address names an episode (or the creator starts
   * one). Bare /dashboard is the creator's home: their series, where each stands, and the way to start one.
   * It used to open straight into an episode editor for a series that did not exist yet, while the explorer
   * beside it said there was nothing there.
   */
  const studioOnScreen = studioIsSurface ? (studioOpen || !!productionParam) : studioOpen;
  /** Leaving every episode address on the workstation (Back, the breadcrumb) returns to the home. */
  const prevProduction = useRef(productionParam);
  useEffect(() => {
    if (prevProduction.current && !productionParam && studioIsSurface) {
      setStudioOpen(false); setInitialStream(null); setOpenStoryboardId(null);
    }
    prevProduction.current = productionParam;
  }, [productionParam, studioIsSurface]);
  /**
   * The chat is a co-pilot RAIL rather than the whole surface whenever the Studio holds the centre. Under
   * /dashboard that is true by definition — the Studio IS the workstation's surface — so we do not wait for
   * the panel to report its expansion. It used to depend solely on that callback, which meant a paint could
   * land with the chat spread across the canvas and no rail beside it.
   */
  const asRail = isDesktopView && (studioExpanded || studioIsSurface);
  /** The empty crew pane only exists where the Studio is the surface — /group is a chat, not a workstation. */
  const contactsRail = studioIsSurface ? paneEmptyState : null;
  /**
   * THE WORKSTATION FRAME IS STRUCTURAL (owner 2026-08-23: "think of the two panes as IDE panes… they always
   * appear"). A pane may be FOLDED, never absent: on the workstation the right pane — or the 3rem rail it
   * folds to — is in the DOM whatever level it is on and whatever the Studio has reported about itself.
   * It used to be mounted only when a list happened to be handed in, so any state that produced none also
   * produced no control to bring the pane back — and the fold is remembered across reloads,
   * so that state was a trap the creator could not get out of without clearing localStorage.
   */
  const paneMounted = !!rightPane && studioIsSurface;
  /** A right pane exists whenever the workstation frame mounts one, or a chat is being narrowed beside it. */
  const railMounted = paneMounted || !!session;
  /**
   * Only THIS production's crew may hand the Studio a roster — any other open chat is unrelated. On the
   * workstation that roster comes from the HOST (which knows the crew chat whether or not it is the one on
   * screen); a Studio opened from inside a chat still reads the chat it is standing in.
   */
  const crewProfileIds = useMemo(
    () => (studioIsSurface ? (crewMemberIds || []) : members.map((m) => m.profile_id).filter((id): id is string => !!id)),
    [studioIsSurface, crewMemberIds, members],
  );

  /**
   * THE RIGHT PANE'S OWN CHROME (owner 2026-08-21: the toggle "is not on video studio pane, but the right
   * pane"). Defined once and rendered whether or not a crew chat is open, so the pane's fold, width and
   * border never change with what is inside it.
   *
   * The toggle sits in a slim 32px row pushed to the pane's inner edge (owner 2026-08-24: *"the icons can
   * have its row to avoid overlapping"*) — left edge here, right edge on the explorer, each facing the canvas
   * it yields space to. Both come from PaneChrome, so they are the same control on both sides.
   */
  const paneEdge = rightPane ? (
    <>
      <PaneResizeHandle side="right" {...rightPane.resize} />
      <PaneToggleRow side="right" onCollapse={rightPane.onToggle} />
    </>
  ) : null;

  /** What the pane folds down to: the way back, plus the one signal a folded chat must not swallow. */
  const paneRail = rightPane && !chatRailOpen ? (
    <PaneRail side="right" onExpand={rightPane.onToggle}>
      {rightPane.unread && <UnreadDot />}
    </PaneRail>
  ) : null;

  /** The pane's geometry: it grows to its own width and hugs the right edge, beside the Studio, never over it. */
  const paneStyle = { maxWidth: paneWidth, marginLeft: 'auto' } as React.CSSProperties;
  /** Slid out = out of the tab order + hidden from screen readers, not merely off-screen. */
  const paneSlide = `${panesDragging ? '' : 'transition-transform duration-200 ease-out'} ${chatRailOpen ? '' : 'translate-x-full invisible'}`;

  const studioPanel = (
    <StudioPanel
      open={studioOnScreen}
      leftRail={sidebarVisible}
      leftWidth={sidebarWidth}
      /* Desktop-under-/dashboard has no X: the Studio is the surface, and closing it would just reveal an
         empty launchpad. Mobile (and an in-chat Studio) keeps the close — there is genuinely something behind. */
      onHome={studioIsSurface ? () => navigate('/dashboard') : undefined}
      unsavedEpisode={productionParam === 'new'}
      onClose={studioIsSurface ? undefined : () => {
        setStudioOpen(false); setInitialStream(null); setOpenStoryboardId(null);
        if (productionParam) navigate('/dashboard', { replace: true });
      }}
      initialIdea={session ? text : undefined}
      initialStream={initialStream}
      openStoryboardId={openStoryboardId}
      initialStage={(stageParam as never) || null}
      onStageChange={goStage}
      onEpisodeCreated={onEpisodeCreated}
      onExpandedChange={setStudioExpanded}
      chatRailOpen={chatRailOpen}
      chatWidth={paneWidth}
      smooth={!panesDragging}
      chatRailAvailable={railMounted}
      onCrewChatResolved={onCrewChatResolved}
      /* The crew link is SERVER-OWNED on the workstation (recruitCrewAgent stamps it), so the Studio carries
         no chat in there; a Studio launched from inside a chat still does. */
      conversationId={studioIsSurface ? null : session?.id || null}
      uid={myUid || ''}
      memberProfileIds={crewProfileIds}
    />
  );

  if (!session) {
    // The /dashboard landing. On the WORKSTATION the Studio IS the dashboard (RF3-WORKSTATION) — you land in
    // the work; StudioHome remains only as the mobile viewer surface and behind the studio while it loads.
    return (
      /* Both branches render [surface, studioPanel] so the Studio keeps its IDENTITY (and the storyboard being
         edited) when the right pane walks between its two levels — index 1 stays the same element type. */
      <>
        <>
          <StudioHome
            onStartProduction={(stream) => {
              setInitialStream(stream); setStudioOpen(true);
              // On the workstation the new episode gets an address, so Back returns to this home.
              if (studioIsSurface) navigate('/dashboard/new/story');
            }}
            onOpenProduction={(sb) => openProduction(sb.slug || sb.id)}
            onOpenChats={onOpenChats}
          />
          {/* The crew pane with nobody in it yet — same strip, same toggle, same border as when a crew chat
              is open, so the pane is one place rather than two. */}
          {paneMounted && (
            <>
              <div
                ref={rightPane?.paneRef}
                data-testid="chat-pane"
                className={`relative hidden md:flex flex-1 flex-col h-full min-w-0 border-l border-secondary-200 dark:border-secondary-600 ${paneSlide}`}
                style={paneStyle}
                aria-hidden={!chatRailOpen}
              >
                {paneEdge}
                {contactsRail}
              </div>
              {paneRail}
            </>
          )}
        </>
        {studioPanel}
      </>
    );
  }

  // You can @-mention agents AND joined humans (not yourself). Humans resolve to their
  // uid; an @human-only message is directed at the person (no agent is forced to reply).
  const mentionables: MemberSpec[] = [
    ...members,
    ...(session.humanMembers || [])
      .filter((h) => h.uid !== myUid)
      .map((h) => ({ id: h.uid, name: h.name, persona: 'Person', avatar_icon: 'user' })),
  ];

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    setMention(detectMention(e.target.value, e.target.selectionStart ?? e.target.value.length));
  };

  const suggestions = mention
    ? mentionables.filter((m) => m.name.toLowerCase().startsWith(mention.query.toLowerCase()))
    : [];

  const pickMention = (m: MemberSpec) => {
    if (!mention) return;
    const caret = taRef.current?.selectionStart ?? text.length;
    setText(applyMention(text, mention, m.name, caret));
    setMention(null);
    taRef.current?.focus();
  };

  const submit = () => {
    const t = text.trim();
    // Allow sending with attachments only, AND while agents are still replying — the
    // user can keep dropping messages; each is picked up on the agent's next turn.
    if (!t && attachments.length === 0) return;
    const mentioned = resolveMentionTargets(t, mentionables);
    const agentTargets = mentioned.filter((id) => members.some((m) => m.id === id));
    // @human(s) but no agent → directed at the person; don't force an agent reply.
    const humanOnly = mentioned.length > 0 && agentTargets.length === 0;
    const sentAttachments = attachments;
    // Attach the pre-generation pickers only when the draft actually asks for that media kind.
    const intent = detectMediaIntent(t);
    const mediaAspect = intent === 'image' || intent === 'video' ? aspect : undefined;
    const mediaSize = intent === 'image' || intent === 'video' ? size : undefined;
    const musicStyle = intent === 'music' ? buildMusicStyle(music) || undefined : undefined;
    const musicKind = intent === 'music' ? buildMusicKind(music) : undefined;
    const durationSec = intent === 'music' ? (music.durationSec || DEFAULT_SONG_LENGTH) : undefined;
    const mediaStyle = (intent === 'image' || intent === 'video') ? style || undefined : undefined;
    const cameraMoveOpt = intent === 'video' ? cameraMove || undefined : undefined; // camera move is video-only
    setText('');
    setMention(null);
    setAttachments([]);
    setAttachError('');
    setMusic({});
    setStyle('');
    setCameraMove('');
    setSize(DEFAULT_SIZE);
    if (speech.listening) speech.stop();
    onSend(t, mentioned.length ? mentioned : undefined, {
      expectReply: !humanOnly,
      attachments: sentAttachments.length ? sentAttachments : undefined,
      mediaAspect,
      mediaSize,
      musicStyle,
      musicKind,
      mediaStyle,
      cameraMove: cameraMoveOpt,
      durationSec,
    });
  };

  // Export the chat as an artifact; the card streams back in via the message feed.
  const handleExport = async (kind: ArtifactKind, format?: 'html') => {
    if (!session || exporting || !onExport) return;
    setExporting(kind);
    setExportErr('');
    try {
      await onExport(kind, format);
    } catch (e: any) {
      setExportErr(e?.message || 'Could not export this chat. Try again.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
    <div
      ref={asRail ? rightPane?.paneRef : undefined}
      data-testid={asRail ? 'chat-pane' : undefined}
      className={`relative flex-1 flex flex-col h-full min-w-0 ${asRail ? `border-l border-secondary-200 dark:border-secondary-600 ${paneSlide}` : ''}`}
      style={asRail ? paneStyle : undefined}
      /* Slid out = not just off-screen but out of the tab order + hidden from screen readers, so keyboard users
         never land in a pane they can't see. React 18 does not forward `inert`, so use the CSS/ARIA equivalent:
         `invisible` drops it from the a11y tree AND removes its descendants from tab order. */
      aria-hidden={asRail && !chatRailOpen}
    >
      {/* LEVEL 2 wears the same pane chrome as LEVEL 1 — fold, drag and border do not change with the level. */}
      {asRail && paneEdge}
      {/* Header: the group members + overflow menu */}
      <ChatThreadHeader
        session={session}
        members={members}
        catalogAvatars={catalogAvatars}
        exporting={exporting}
        exportErr={exportErr}
        onBack={onBack}
        /* NO WAY BACK ON THE WORKSTATION any more (owner 2026-08-23): the pane has one level. It is the crew
           of the production you have open, and you change it by navigating the left pane — so a back arrow
           would lead to a list that no longer exists. Mobile keeps its own back to the launchpad. */
        backOnDesktop={false}
        onManage={onManage}
        onInvite={onInvite}
        onRequests={isOwner ? () => setNotesOpen(true) : undefined}
        onExport={handleExport}
        onExportTranscript={(format) => session && downloadTranscript(session, format, { branded })}
        onShowDecisions={() => setShowBrief(true)}
      />

      {/* Stream — memoized so typing in the composer never re-renders/re-parses the message list */}
      <MessageStream
        session={session}
        myUid={myUid}
        members={members}
        memberNames={memberNames}
        catalogAvatars={catalogAvatars}
        isOwner={isOwner}
        responding={responding}
        sending={sending}
        repliedSince={repliedSince}
        endRef={endRef}
        onReact={onReact}
        onBookmark={onBookmark}
        onDeleteMessage={onDeleteMessage}
        onWalletChanged={onWalletChanged}
        onDownloadArtifact={onDownloadArtifact}
        onSend={onSend}
        onAddAgent={handleAddAgent}
        onSelectAgent={selectAgent}
        onMention={insertMention}
      />

      {/* Non-intrusive activity bar — what the agents are doing on this turn */}
      <ChatActivityBar activity={activity} />

      {/* Retention nudge — soft, dismissible upgrade invite once a FREE chat gets long */}
      {session && <RetentionNudge chatId={session.id} messageCount={session.messages.length} />}

      {/* Composer — drag a file anywhere onto it, or use the attach (paperclip) button */}
      <div
        className="relative px-4 md:px-6 py-4 border-t border-secondary-200 dark:border-secondary-600 shrink-0"
        onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(Array.from(e.dataTransfer.files || [])); }}
      >
        {dragging && (
          <div className="absolute inset-2 z-20 rounded-2xl border-2 border-dashed border-secondary-400 dark:border-secondary-500 bg-white/80 dark:bg-black/80 flex items-center justify-center pointer-events-none">
            <p className={`${TYPE.control} text-secondary-600 dark:text-secondary-300 flex items-center gap-2`}>
              <Paperclip className="w-4 h-4" /> Drop files to attach (PDF, CSV, TXT, MD, DOCX, images · ≤10 MB)
            </p>
          </div>
        )}

        {/* Attachment chips + validation error */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((a, i) => {
              const Icon = KIND_ICON[a.kind];
              return (
                <span key={i} className={`inline-flex items-center gap-1.5 max-w-[14rem] pl-2 pr-1 py-1 rounded-lg bg-secondary-100 dark:bg-secondary-800 ${TYPE.control} text-secondary-700 dark:text-secondary-200`}>
                  <Icon className="w-3.5 h-3.5 shrink-0 text-secondary-500" />
                  <span className="truncate">{a.file.name}</span>
                  <span className="text-secondary-400 shrink-0">{formatBytes(a.file.size)}</span>
                  <button onClick={() => removeAttachment(i)} aria-label={`Remove ${a.file.name}`} title={`Remove ${a.file.name}`} className="p-0.5 rounded hover:bg-secondary-200 dark:hover:bg-secondary-700 shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
        {/* What happens to an attached image — the behaviour, not its price (owner 2026-08-22). */}
        {attachments.some((a) => a.kind === 'image') && (
          <p className={`mb-2 ${TYPE.meta} text-secondary-400 flex items-center gap-1`}>
            <Eye className="w-3 h-3 shrink-0" />
            Your crew reads attached images for text and visuals.
          </p>
        )}
        {attachError && <p className={`mb-2 ${TYPE.control} text-secondary-500`}>{attachError}</p>}

        {/* Animate-this (image-to-video) — appears on an attached image; prefills a motion cue so the
            router turns that exact frame into a short clip (consistent character). Ship-dark behind the flag. */}
        {I2V_ENABLED && attachments.some((a) => a.kind === 'image') && (
          <button
            type="button"
            onClick={() => setText((t) => (ANIMATE_CUE.test(t) ? t : `Animate this${t.trim() ? ` — ${t.trim()}` : ' — '}`))}
            title="Turn this image into a short video clip (describe the motion)"
            className={`inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full ${TYPE.control} border border-secondary-200 dark:border-secondary-600 text-secondary-600 dark:text-secondary-300 hover:border-secondary-400 transition-colors`}
          >
            <Clapperboard className="w-3.5 h-3.5" /> Animate this image
          </button>
        )}

        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.pptx,.xlsx,.txt,.md,.markdown,.csv,.tsv,.json,.png,.jpg,.jpeg,.webp,image/*,application/pdf,text/plain,text/markdown,text/csv"
          className="hidden"
          onChange={(e) => { addFiles(Array.from(e.target.files || [])); if (fileRef.current) fileRef.current.value = ''; }}
        />

        {/* Pre-generation pickers — appear only when the draft asks for image/video/music */}
        <GenerationOptions
          intent={detectMediaIntent(text)}
          aspect={aspect}
          style={style}
          onStyle={setStyle}
          cameraMove={cameraMove}
          onCameraMove={setCameraMove}
          onAspect={setAspect}
          size={size}
          onSize={setSize}
          music={music}
          onMusic={setMusic}
        />

        <div className="relative flex items-end gap-1 rounded-2xl border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-900 pl-1.5 pr-1.5 py-1 focus-within:border-secondary-500 transition-colors">
          {/* Create + Attach stacked vertically into one column so they take a single button's
              width — leaving the message input the horizontal room it needs. */}
          <div className="flex flex-col shrink-0 gap-0.5 self-end mb-0.5">
            {/* + Create — seeds the composer with a starter so the media pickers appear (discovery) */}
            <CreateMenu onPick={(starter) => { setText(starter); taRef.current?.focus(); }} onVideoStudio={() => setStudioOpen(true)} />
            {/* Attach (paperclip) */}
            <button
              onClick={() => fileRef.current?.click()}
              aria-label="Attach files"
              title="Attach files for reference"
              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
            >
              <Paperclip className="w-4 h-4" />
            </button>
          </div>
          {/* @mention autocomplete */}
          {mention && suggestions.length > 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-600 rounded-xl shadow-xl overflow-hidden z-10">
              <p className={`px-3 py-1.5 ${TYPE.label} text-secondary-400 flex items-center gap-1`}>
                <AtSign className="w-3 h-3" /> Direct to
              </p>
              {suggestions.map((m) => (
                <button
                  key={m.id}
                  onClick={() => pickMention(m)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-secondary-100 dark:hover:bg-secondary-700"
                >
                  <MemberAvatar avatarIcon={m.avatar_icon} avatarPath={m.avatar_path} avatarDriveFileId={m.avatar_drive_file_id} avatarImage={m.profile_id ? catalogAvatars[m.profile_id] : null} conversationId={session.id} name={m.name} className="w-5 h-5" />
                  <span className={`${TYPE.control} text-secondary-900 dark:text-white`}>{m.name}</span>
                  <span className={`${TYPE.meta} text-secondary-400`}>{m.persona}</span>
                </button>
              ))}
            </div>
          )}

          <textarea
            ref={taRef}
            value={text}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setMention(null);
              if (e.key === 'Enter' && !e.shiftKey && !mention) { e.preventDefault(); submit(); }
            }}
            onPaste={(e) => {
              const files = Array.from(e.clipboardData?.files || []);
              if (files.length) { e.preventDefault(); addFiles(files); }
            }}
            rows={2}
            placeholder="Message the group… use @ to direct it to one specialist"
            /* 13px, matching the bubbles: a DELIBERATE exception to the type scale (owner 2026-08-22) so the
               composer sets your words at exactly the size the thread will show them. Do not scale-sweep. */
            className="flex-1 resize-none bg-transparent border-0 py-2 min-h-[52px] max-h-40 text-[13px] leading-relaxed text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:outline-none focus:ring-0"
          />
          {/* One right-hand action (WeChat/WhatsApp style): mic while empty, send once
              there's text or an attachment — a single button, so the bar stays compact. */}
          {text.trim() || attachments.length > 0 || !speech.supported ? (
            <button
              onClick={submit}
              disabled={!text.trim() && attachments.length === 0}
              className="w-9 h-9 mb-0.5 shrink-0 rounded-full bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 flex items-center justify-center disabled:opacity-40 hover:bg-black dark:hover:bg-secondary-100 transition-colors"
              aria-label="Send" title="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => { speech.toggle(); taRef.current?.focus(); }}
              aria-label={speech.listening ? 'Stop voice input' : 'Start voice input'}
              aria-pressed={speech.listening}
              title={speech.listening ? 'Listening… tap to stop' : 'Voice input'}
              className={`w-9 h-9 mb-0.5 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                speech.listening
                  ? 'bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 animate-pulse'
                  : 'text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800'
              }`}
            >
              <Mic className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <GroupNotesPanel open={notesOpen} conversationId={session?.id || null} asPage={!isDesktopView} onClose={() => setNotesOpen(false)} />

      <AgentDetailPanel
        open={!!agentDetail}
        session={session}
        member={agentDetail}
        isOwner={!!isOwner}
        onWalletChanged={onWalletChanged}
        onClose={() => setAgentDetail(null)}
        onUpdated={(c) => { onUpdated?.(c); setAgentDetail(null); }}
      />

      {session && <DecisionBriefPanel open={showBrief} conversationId={session.id} onClose={() => setShowBrief(false)} />}
    </div>
    {asRail && paneRail}
    {/* The Studio is a SIBLING of the sliding rail, never a child: the rail's slide-out uses `translate` +
        `invisible`, and a transformed ancestor becomes the containing block for `position: fixed` while
        `visibility: hidden` inherits — so hiding the chat used to shove the Studio sideways and blank it
        (BUGS.md 2026-08-16). Sliding the chat away must GIVE the Studio the screen, not take it. */}
    {studioPanel}
    </>
  );
};

export default ChatThread;
